/**
 * The admin calendar's day map. The public calendar shows *whether* a day is
 * taken and never by whom (NFR-6); the admin one exists to show exactly who —
 * so this expands named bookings and reasoned blackouts into per-day entries.
 */

import {
  type BookingStatus,
  compareDates,
  occupiesDates,
  type PlainDate,
  plainDate,
} from "@bivak/domain";

export type KalenderBoeking = {
  id: string;
  partyName: string;
  status: string;
  arrivalDate: string;
  departureDate: string;
};

export type KalenderBlokkade = {
  from: string;
  to: string;
  kind: "blackout" | "maintenance";
  reason: string | null;
};

export type DagItem =
  | {
      soort: "boeking";
      id: string;
      naam: string;
      status: BookingStatus;
      aankomst: boolean;
      vertrek: boolean;
    }
  | { soort: "blokkade"; kind: "blackout" | "maintenance"; reason: string | null };

/**
 * Bookings span arrival THROUGH departure (both shown — the departure day is
 * the wisseldag, half of it is still theirs); blackout ranges are the
 * database's half-open daterange, so `to` itself is free again.
 */
export function bezettingPerDag(
  bookings: readonly KalenderBoeking[],
  blokkades: readonly KalenderBlokkade[],
): Map<string, DagItem[]> {
  const dagen = new Map<string, DagItem[]>();
  const voegToe = (iso: string, item: DagItem) => {
    const bestaand = dagen.get(iso);
    if (bestaand) bestaand.push(item);
    else dagen.set(iso, [item]);
  };

  for (const booking of bookings) {
    // An enquiry holds no dates (several groups may ask about one weekend);
    // the domain's occupancy rule decides, not a status list kept here.
    if (!occupiesDates(booking.status as BookingStatus)) continue;
    let dag = plainDate(booking.arrivalDate);
    const laatste = plainDate(booking.departureDate);
    while (true) {
      const iso = dag.toString();
      voegToe(iso, {
        soort: "boeking",
        id: booking.id,
        naam: booking.partyName,
        status: booking.status as BookingStatus,
        aankomst: iso === booking.arrivalDate,
        vertrek: iso === booking.departureDate,
      });
      if (dag.equals(laatste)) break;
      dag = dag.add({ days: 1 });
    }
  }

  for (const blokkade of blokkades) {
    let dag = plainDate(blokkade.from);
    const einde = plainDate(blokkade.to);
    while (compareDates(dag, einde) < 0) {
      voegToe(dag.toString(), {
        soort: "blokkade",
        kind: blokkade.kind,
        reason: blokkade.reason,
      });
      dag = dag.add({ days: 1 });
    }
  }

  return dagen;
}

function vroegste(a: PlainDate, b: PlainDate): PlainDate {
  return compareDates(a, b) <= 0 ? a : b;
}
function laatste(a: PlainDate, b: PlainDate): PlainDate {
  return compareDates(a, b) >= 0 ? a : b;
}

/**
 * Which month grids to render. TODAY is the anchor: at most 3 months of
 * history, at most 17 months ahead, and when both sides overflow the 18-grid
 * cap the future wins. Anchoring on the data instead sounds nicer but fails
 * after two seasons of accumulated settled bookings — the oldest record would
 * push the current month clean off the screen.
 */
export function kalenderMaanden(dagen: Map<string, DagItem[]>, todayIso: string): PlainDate[] {
  const isos = [...dagen.keys()].sort();
  const vandaag = plainDate(todayIso).with({ day: 1 });
  const eersteData = isos[0] ? plainDate(isos[0] as string).with({ day: 1 }) : vandaag;
  const laatsteData = isos.at(-1) ? plainDate(isos.at(-1) as string).with({ day: 1 }) : vandaag;

  const einde = vroegste(laatste(laatsteData, vandaag), vandaag.add({ months: 17 }));
  let start = laatste(vroegste(eersteData, vandaag), vandaag.add({ months: -3 }));
  const span = (einde.year - start.year) * 12 + (einde.month - start.month);
  if (span > 17) start = einde.add({ months: -17 });

  const maanden: PlainDate[] = [];
  let maand = start;
  while (compareDates(maand, einde) <= 0) {
    maanden.push(maand);
    maand = maand.add({ months: 1 });
  }
  return maanden;
}
